import AudurSelect from '@kvika/audur-select'
import { Colors } from '@kvika/audur-theme'
import { useState } from 'react'

import { ChevronDown } from '../../../../public/icons/Icons'
import { AudurButton } from '../../../components/audurButtons'
import AudurTooltip from '../../../components/audurTooltip/AudurTooltip'
import PillButtons from '../../../components/pillButtons/PillButtons'
import ExtendedSlider from '../../../components/slider/ExtendedSlider'
import { CalculatorPrimary } from '../../../prismic/prismicTypes'
import { TranslatedStrings } from '../../../utils/translations/TranslatedStrings'
import { formatNumberIS, formatNumberISFloat } from '../../../utils/Utils'
import CalculatorInputWithRadio from '../components/calculatorInputWithRadio'
import {
	CalculatorDataSettlementCosts,
	GetMortgageSettingsResponse,
	LoanCalculationInputs,
	LoanTypeEnum,
} from '../newCalculator.models'

import styles from './newCalculatorContainers.module.scss'

type INewCalculatorFinancialPlan = {
	onInputValueChange: (value: number | boolean | string, propertyName: keyof LoanCalculationInputs) => void
	totalPaid: number
	onExpand: () => void
	isExpanded: boolean
	objectName: LoanTypeEnum
	showOnMobile: boolean
	temporaryReduction?: number
	durationOfReduction?: number

	estimatedPropertyAnnualIncrease: number
	isPaymentReductionPlan: boolean

	calculatorPrismicTooltipData: CalculatorPrimary
	defaultOptions?: GetMortgageSettingsResponse | null
	settlementCosts: CalculatorDataSettlementCosts[]

	ahk: number
	loanAmount: number
	paymentReductionMinAmount?: number
	paymentReductionMaxAmount?: number
	loanTerm?: number
	interestRateOptionFixed: boolean
}

type INewCalculatorFinancialPlanProps = LoanCalculationInputs & INewCalculatorFinancialPlan

const NewCalculatorFinancialPlan = (props: INewCalculatorFinancialPlanProps) => {
	const { newCalculator: newCalculatorStrings } = TranslatedStrings.is
	const [isExpandedFee, setIsExpandedFee] = useState(false)
	const {
		onInputValueChange,
		objectName,
		totalPaid,
		ahk,
		onExpand,
		isExpanded: isSelected,
		calculatorPrismicTooltipData,
		fixedInterestPercentage: interest,
		extraMonthlyPaymentToPrincipal: extraPaymentsToPrincipal,
		principalPaymentType,
		showOnMobile,
		defaultOptions,
		estimatedPropertyAnnualIncrease,
		isPaymentReductionPlan,
		temporaryReduction,
		durationOfReduction,
		settlementCosts,
		loanAmount,
		paymentReductionMinAmount,
		paymentReductionMaxAmount,
		interestRateOptionFixed,
		loanTerm = 40,
	} = props

	const listOfProps = [
		{
			label: newCalculatorStrings.annualPercentageCost,
			value: `${ahk > 1 ? formatNumberISFloat(ahk) : formatNumberISFloat(ahk * 100)}%`,
			tooltip: calculatorPrismicTooltipData.ahk_tooltip ?? newCalculatorStrings.defaultAhkTooltip,
			alwaysShow: true,
			totalCost: false,
		},
		{
			label: newCalculatorStrings.borrowingCost,
			value: `${formatNumberIS(settlementCosts.reduce((sum, item) => sum + item.amount, 0))} kr.`,
			tooltip: calculatorPrismicTooltipData.borrowing_cost_tooltip ?? newCalculatorStrings.defaultBorrowingCostTooltip,
			totalCost: true,
			alwaysShow: true,
		},
		...settlementCosts.map((item) => ({
			label: item.displayName ?? '',
			value: `${formatNumberIS(item.amount)} kr.`,
			tooltip: '',
			alwaysShow: false,
			totalCost: false,
		})),
		{
			label: newCalculatorStrings.loanAmountMinusCosts,
			value: `${formatNumberIS(
				loanAmount -
					settlementCosts
						.filter((x) => x.displayName === 'Lántökugjald' || x.displayName === 'Skjalagerð')
						.reduce((sum, item) => sum + item.amount, 0)
			)} kr.`,
			tooltip: '',
			alwaysShow: true,
			totalCost: false,
		},
	]

	const stringToDecimal = (value: string) => {
		const parsedValue = parseFloat(value.replace(',', '.'))
		return Number.isNaN(parsedValue) ? 0 : parsedValue
	}

	return (
		<div className={[styles.financialPlan, showOnMobile ? styles.showOnMobile : ''].join(' ')}>
			<div className={styles.summaryInputs}>
				{!isPaymentReductionPlan ? (
					<div className={styles.summaryPlan}>
						<div className={styles.paymentTooltip}>
							{newCalculatorStrings.paymentAgreements}
							<AudurTooltip
								text={
									calculatorPrismicTooltipData.payment_agreements_tooltip ??
									newCalculatorStrings.defaultPaymentAgreementsTooltip
								}
								id='payment_agreements_tooltip'
								infoOutlinedColor={Colors.LightGray}
								activeInfoOutlinedColor={Colors.Silver}
								backgroundColor={Colors.Silver}
								hideCloseIcon
								hideTitleRow
							/>
						</div>
						<PillButtons
							items={[
								{
									id: 'equalPayments',
									label: newCalculatorStrings.paymentArrangementOptionEqualPayments,
									isSelected: principalPaymentType === 'EqualPayments',
									onClick: () => onInputValueChange('EqualPayments', 'principalPaymentType'),
								},
								{
									id: 'equalPrincipalPayments',
									label: newCalculatorStrings.paymentArrangementOptionEqualPrincipalPayments,
									isSelected: principalPaymentType === 'EqualPrincipal',
									onClick: () => onInputValueChange('EqualPrincipal', 'principalPaymentType'),
								},
							]}
							style={{ gap: '14px' }}
							mobileFullWidth
						/>
					</div>
				) : (
					<>
						<ExtendedSlider
							value={temporaryReduction! ?? 0}
							min={paymentReductionMinAmount ?? 5}
							max={paymentReductionMaxAmount ?? 120000}
							id={styles.temporaryReduction}
							label={TranslatedStrings.is.newCalculator.temporaryReduction}
							onChange={(value) => onInputValueChange(value, 'optionBAmount')}
							valueSuffix=' kr.'
							minLabelFunction={(value) => `${formatNumberIS(value)} kr.`}
							maxLabelFunction={(value) => `${formatNumberIS(value)} kr.`}
							step={1000}
							tooltip={
								calculatorPrismicTooltipData.temporary_reduction_length_tooltip ??
								TranslatedStrings.is.newCalculator.defaultTemporaryReductionTooltip
							}
						/>
						<div className={styles.selectWrapper}>
							<AudurSelect
								id={styles.durationOfReduction}
								instanceId='Lengd'
								inputId='Lengd'
								label={TranslatedStrings.is.newCalculator.durationOfReduction}

								onChange={(value: any) => onInputValueChange(value?.value ?? 0, 'optionBLengthInYears')}
								value={
									durationOfReduction ? { value: durationOfReduction, label: `${durationOfReduction} ár` } : undefined
								}
								options={
									defaultOptions?.options.paymentReductionMaxLengthMonths
										? Array.from(
												{ length: defaultOptions.options.paymentReductionMaxLengthMonths / 12 },
												(_, i) => i + 1
											).map((year) => ({
												value: year,
												label: `${year} ár`,
											}))
										: [
												{ value: 1, label: '1 ár' },
												{ value: 2, label: '2 ár' },
												{ value: 3, label: '3 ár' },
												{ value: 4, label: '4 ár' },
												{ value: 5, label: '5 ár' },
											]
								}
							/>
							<AudurTooltip
								text={
									calculatorPrismicTooltipData.temporary_reduction_length_tooltip ??
									TranslatedStrings.is.newCalculator.defaultTemporaryReductionLengthTooltip
								}
								id='select_temporary_reduction_length_tooltip'
								infoOutlinedColor={Colors.LightGray}
								activeInfoOutlinedColor={Colors.Silver}
								backgroundColor={Colors.Silver}
								hideCloseIcon
								hideTitleRow
							/>
						</div>
					</>
				)}
			</div>
			<CalculatorInputWithRadio
				value={interest}
				getIsAllowedValue={() => true}
				width='100%'
				name={newCalculatorStrings.interest}
				label={newCalculatorStrings.interest}
				onChange={(event) => onInputValueChange(stringToDecimal(event.target.value), 'fixedInterestPercentage')}
				decimalScale={2}
				suffix='%'
				min={(defaultOptions && defaultOptions?.options?.paymentReductionInterestRateMinLimit) ?? 3.0}
				max={100}
				tooltip={calculatorPrismicTooltipData.interest_tooltip ?? newCalculatorStrings.defaultInterestTooltip}
				options={[
					{
						checked: interestRateOptionFixed,
						label: newCalculatorStrings.interestTodayOption1,
						name: `interestOption1-${objectName}`,
						onChange: () => {
							onInputValueChange(true, 'interestRateOptionFixed')
						},
						value: 1,
						tooltip:
							calculatorPrismicTooltipData.interest_tooltip_fixed_option ??
							newCalculatorStrings.defaultInterestOption1Tooltip,
					},
					{
						checked: !interestRateOptionFixed,
						label: newCalculatorStrings.interestTodayOption2,
						name: `interestOption2-${objectName}`,
						onChange: () => {
							onInputValueChange(false, 'interestRateOptionFixed')
						},
						value: 1,
						tooltip:
							calculatorPrismicTooltipData.interest_tooltip_3year_option ??
							newCalculatorStrings.defaultInterestOption2Tooltip,
					},
				]}
			/>
			<CalculatorInputWithRadio
				value={extraPaymentsToPrincipal}
				getIsAllowedValue={() => true}
				width='100%'
				name={newCalculatorStrings.taxFreeAllocation}
				label={newCalculatorStrings.taxFreeAllocationLabel}
				onChange={(event) => onInputValueChange(stringToDecimal(event.target.value), 'extraMonthlyPaymentToPrincipal')}
				suffix=' kr.'
				min={0}
				tooltip={
					calculatorPrismicTooltipData.tex_free_allocation_tooltip_with_link ??
					newCalculatorStrings.defaultTax_freeAllocationTooltip
				}
			/>
			<div className={styles.paymentArrangement}>
				<CalculatorInputWithRadio
					value={estimatedPropertyAnnualIncrease}
					getIsAllowedValue={() => true}
					width='100%'
					name={TranslatedStrings.is.newCalculator.estimatedPropertyAnnualIncrease}
					label={TranslatedStrings.is.newCalculator.estimatedPropertyAnnualIncrease}
					onChange={(event) =>
						onInputValueChange(stringToDecimal(event.target.value), 'annualHousingPriceIncreasePercentage')
					}
					decimalScale={2}
					suffix='%'
					min={-99.99}
					max={99.99}
					defaultValue={0}
					tooltip={
						calculatorPrismicTooltipData.estimated_property_annual_increase_tooltip ??
						TranslatedStrings.is.newCalculator.defaultEstimatedPropertyAnnualIncreaseTooltip
					}
					allowNegative
				/>
			</div>
			<div className={styles.dataInfoApplication}>
				{listOfProps.map((item) => {
					const isHidden = !item.alwaysShow

					if (!isExpandedFee && isHidden) {
						return null // Skip hidden items
					}

					return (
						<div
							className={[
								styles.dataInfoCell,
								!item.alwaysShow ? styles.expandableItem : '',
								item.totalCost ? styles.totalCost : '',
								isExpandedFee ? styles.expandedList : '',
							].join(' ')}
							key={item.label}
						>
							<div>
								{item.totalCost && (
									<AudurButton
										variant='text'
										onClick={() => setIsExpandedFee((prev) => !prev)}
										className={styles.expandButton}
										icon={<ChevronDown style={{ transform: isExpandedFee ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
										iconPlacement='right'
									>
										{item.label}
									</AudurButton>
								)}
								{!item.totalCost && item.label}
								{item.tooltip && item.tooltip.length > 0 && (
									<AudurTooltip
										text={item.tooltip}
										id={`${item.label}_tooltip`}
										infoOutlinedColor={Colors.LightGray}
										activeInfoOutlinedColor={Colors.Silver}
										backgroundColor={Colors.Silver}
										hideCloseIcon
										hideTitleRow
									/>
								)}
							</div>
							{item.value}
						</div>
					)
				})}
			</div>

			<div className={styles.totalPaidCell}>
				<div className={styles.totalPaidHeader}>
					{isPaymentReductionPlan
						? newCalculatorStrings.totalPaidHeaderLighter.replace('XX', loanTerm.toString())
						: newCalculatorStrings.totalPaidHeaderFaster.replace('XX', loanTerm.toString())}
				</div>
				<div className={styles.totalPaidValue}>{formatNumberIS(totalPaid)} kr.</div>
				<PillButtons
					items={[
						{
							id: isPaymentReductionPlan ? 'paymentReduction' : 'assetFormation',
							label: newCalculatorStrings.showPaymentPlan,
							isSelected,
							onClick: onExpand,
						},
					]}
				/>
			</div>
		</div>
	)
}

export default NewCalculatorFinancialPlan
